import type { ReportView } from './components/ViewSwitcher'
import { getTemplate } from './templates'
import { Persona, ReportIconKey, ViewConfig } from './view-config'

/**
 * Dashboards: named compositions of report views rendered as a tile grid.
 * Pure data, same philosophy as templates.ts — a Dashboard is serializable
 * and is the candidate contract for future backend-stored dashboards.
 * Tiles embed full ViewConfigs so a tile can later carry knob tweaks
 * without referencing a template.
 */

/**
 * Authoring shorthand for a tile's width. Not used to render directly — it
 * seeds the initial grid layout (see SIZE_TO_W / packTileLayout); the actual
 * on-screen size is the tile's free-form grid `layout` (drag/resize snaps it).
 */
export type TileSize = 'quarter' | 'third' | 'half' | 'three-quarter' | 'full'

/** Authoring shorthand for a tile's height; seeds the initial layout height. */
export type TileHeight = 'short' | 'medium' | 'tall'

/**
 * Free-form placement of a tile on the dashboard grid, in grid cells (the
 * Retool-style "grid paper" model). x/y are the top-left cell; w/h the span.
 * Edited by dragging/resizing in the builder and persisted with the dashboard.
 */
export type TileLayout = { x: number; y: number; w: number; h: number }

/** Columns in the dashboard grid — tiles snap to these (12 ⇒ clean 1/4 steps). */
export const GRID_COLS = 12

/**
 * Height of one grid row in px; a tile's pixel height is `h × this` (+ margins).
 * Halved from 48 → 24 to double the vertical snap resolution: tiles size in
 * ~24px steps instead of ~48px, so a metric tile can be trimmed close to its
 * content. All stored `h` values were doubled to keep physical sizes constant.
 */
export const GRID_ROW_HEIGHT = 24

/**
 * Grid item margin `[x, y]` in px (gap between tiles). The y-gap is halved
 * (12 → 6) because doubling `h` would otherwise double the margin baked between
 * the now-twice-as-many sub-rows; 6 keeps multi-row tiles their original height.
 */
export const GRID_MARGIN: [number, number] = [12, 6]

/** Width (in grid columns) each size shorthand seeds. */
export const SIZE_TO_W: Record<TileSize, number> = {
  quarter: 3,
  third: 4,
  half: 6,
  'three-quarter': 9,
  full: 12,
}

/** Height (in grid rows) each height shorthand seeds (24px rows; see GRID_ROW_HEIGHT). */
export const HEIGHT_TO_H: Record<TileHeight, number> = {
  // 6 rows (~174px) fit a stat card's label + number + threshold badge snugly —
  // trimmed from the old 8-equivalent, which left dead space below the number.
  short: 6,
  medium: 12,
  tall: 16,
}

/**
 * Sensible default width for a tile based on its content: one big number is
 * small (25%), raw lists and multi-series charts need the full row, and a
 * single-series chart / ranked reads well at half.
 */
export function defaultTileSize(config: ViewConfig): TileSize {
  if (config.kind === 'aggregate' && !config.groupBy) return 'quarter'
  if (config.kind === 'list') return 'full'
  if (config.seriesBy) return 'full'
  return 'half'
}

/**
 * Sensible default height by content: a single metric stays short, a raw list
 * wants the tall body, everything else (charts) reads well at medium.
 */
export function defaultTileHeight(config: ViewConfig): TileHeight {
  if (config.kind === 'aggregate' && !config.groupBy) return 'short'
  if (config.kind === 'list') return 'tall'
  return 'medium'
}

export type DashboardTile = {
  config: ViewConfig
  /** Authoring shorthand for width; seeds the initial grid layout. */
  size: TileSize
  /**
   * How this report is shown *on this dashboard* — a per-tile override that does
   * NOT change the underlying report's own saved view. When unset, the tile
   * picks a sensible default from the report's shape (see effectiveTileView in
   * DashboardTile.tsx). Must be one of `availableViews(config, result)` for the
   * report's shape; an out-of-shape value falls back to the default.
   */
  view?: ReportView
  /** Authoring shorthand for height; seeds the initial grid layout. */
  height?: TileHeight
  /**
   * Free-form grid placement. Present on dashboards arranged in the builder;
   * absent on shipped dashboards (authored with size/height) — packTileLayout
   * derives an initial layout from the shorthand in that case.
   */
  layout?: TileLayout
}

/**
 * Derive an initial grid layout for tiles that don't carry one: flow each tile
 * left-to-right at its size→width / height→height, wrapping to a new row when it
 * would overflow GRID_COLS. Deterministic, so shipped dashboards render the same
 * arrangement every time until a user drags them in the builder.
 *
 * @param tiles - the dashboard's tiles, in authoring order
 * @returns one TileLayout per tile, index-aligned with `tiles`
 */
export function packTileLayout(tiles: DashboardTile[]): TileLayout[] {
  let x = 0
  let rowTop = 0
  let rowHeight = 0
  return tiles.map((t) => {
    const w = SIZE_TO_W[t.size]
    const h = HEIGHT_TO_H[t.height ?? defaultTileHeight(t.config)]
    if (x + w > GRID_COLS) {
      x = 0
      rowTop += rowHeight
      rowHeight = 0
    }
    const placed: TileLayout = { x, y: rowTop, w, h }
    x += w
    rowHeight = Math.max(rowHeight, h)
    return placed
  })
}

export type Dashboard = {
  id: string
  name: string
  description: string
  /** Library-card icon; falls back to a generic dashboard glyph when unset. */
  icon?: ReportIconKey
  /**
   * Demo personas this dashboard is curated for. `['rep']` → shown only in the
   * rep persona; untagged → team-wide (admin). See persona.ts/visibleForPersona.
   */
  personas?: Persona[]
  tiles: DashboardTile[]
}

/**
 * Builds a tile from a shipped template. Throws on a typo'd id so tests catch it.
 * Pass `layout` to pin an exact grid placement (a dashboard arranged in the
 * builder); omit it to let packTileLayout flow the tile from its `size`.
 */
const tile = (
  templateId: string,
  size: TileSize,
  view?: ReportView,
  layout?: TileLayout
): DashboardTile => {
  const config = getTemplate(templateId)
  if (!config) throw new Error(`Unknown template id: ${templateId}`)
  return { config, size, view, ...(layout && { layout }) }
}

export const DASHBOARDS: Dashboard[] = [
  {
    id: 'team-performance',
    name: 'Team Performance',
    description: 'Rep output, closed business and pipeline across the team.',
    icon: 'users',
    tiles: [
      tile('total-deals', 'half', undefined, { x: 0, y: 0, w: 6, h: 4 }),
      tile('closed-revenue-vs-target', 'half', undefined, { x: 6, y: 0, w: 6, h: 4 }), // prettier-ignore
      tile('pipeline-by-stage', 'half', undefined, { x: 0, y: 4, w: 6, h: 12 }),
      tile('top-by-volume', 'half', undefined, { x: 6, y: 4, w: 6, h: 12 }),
      tile('deals-created-monthly', 'full', undefined, { x: 0, y: 16, w: 12, h: 12 }), // prettier-ignore
      tile('deals-created-by-rep', 'full', undefined, { x: 0, y: 28, w: 12, h: 12 }), // prettier-ignore
    ],
  },
  {
    id: 'my-pipeline',
    name: 'My Pipeline',
    description: 'My quota, deals and anything going stale.',
    icon: 'handshake',
    personas: ['rep'],
    tiles: [
      tile('my-quota', 'third'),
      tile('my-open-pipeline', 'third'),
      tile('my-win-rate', 'third'),
      tile('my-pipeline-by-stage', 'full'),
      tile('my-stale-deals', 'half'),
      tile('my-open-deals', 'half'),
    ],
  },
  {
    id: 'tm-admin',
    name: 'TM Admin Dashboard',
    description: 'TM admin view — open pipeline, wins and deal activity.',
    icon: 'dashboard',
    tiles: [
      // Row 1 — four metric tiles.
      tile('tm-open-monthly-volume', 'quarter', undefined, { x: 0, y: 0, w: 3, h: 4 }), // prettier-ignore
      tile('tm-open-customer-opps', 'quarter', undefined, { x: 3, y: 0, w: 3, h: 4 }), // prettier-ignore
      tile('tm-deals-volume-won-this-year', 'quarter', undefined, { x: 6, y: 0, w: 3, h: 4 }), // prettier-ignore
      tile('tm-deals-won-this-year', 'quarter', undefined, { x: 9, y: 0, w: 3, h: 4 }), // prettier-ignore
      // Row 2 — closed counts vs volume.
      tile('tm-closed-deals-2026', 'half', undefined, { x: 0, y: 4, w: 6, h: 13 }), // prettier-ignore
      tile('tm-closed-volume-2026', 'half', undefined, { x: 6, y: 4, w: 6, h: 13 }), // prettier-ignore
      // Row 3 — by-rep wins.
      tile('tm-deals-won-by-rep-2026', 'half', 'horizontal-bar', { x: 0, y: 17, w: 6, h: 13 }), // prettier-ignore
      tile('tm-won-lbs-by-rep-2026', 'half', 'horizontal-bar', { x: 6, y: 17, w: 6, h: 13 }), // prettier-ignore
      // Row 4 — deal activity, total and by rep.
      tile('tm-monthly-deal-activity', 'half', 'line', { x: 0, y: 30, w: 6, h: 14 }), // prettier-ignore
      tile('tm-deal-activity-by-rep-2026', 'half', 'horizontal-bar', { x: 6, y: 30, w: 6, h: 14 }), // prettier-ignore
    ],
  },
  {
    id: 'tm-rep',
    name: 'TM Rep Dashboard',
    description: 'My open pipeline, wins and deal activity vs the team goals.',
    icon: 'dashboard',
    personas: ['rep'],
    tiles: [
      // Row 1 — four metric tiles.
      tile('tm-rep-open-monthly-volume', 'quarter', undefined, { x: 0, y: 0, w: 3, h: 4 }), // prettier-ignore
      tile('tm-rep-open-customer-opps', 'quarter', undefined, { x: 3, y: 0, w: 3, h: 4 }), // prettier-ignore
      tile('tm-rep-deal-lbs-won-2026', 'quarter', undefined, { x: 6, y: 0, w: 3, h: 4 }), // prettier-ignore
      tile('tm-rep-deals-won-2026', 'quarter', undefined, { x: 9, y: 0, w: 3, h: 4 }), // prettier-ignore
      // Row 2 — running won counts vs volume.
      tile('tm-rep-deals-won-running', 'half', undefined, { x: 0, y: 4, w: 6, h: 13 }), // prettier-ignore
      tile('tm-rep-closed-volume-2026', 'half', undefined, { x: 6, y: 4, w: 6, h: 13 }), // prettier-ignore
      // Row 3 — my activity + deals by sales stage.
      tile('tm-rep-monthly-deal-activity', 'half', 'line', { x: 0, y: 17, w: 6, h: 14 }), // prettier-ignore
      tile('my-pipeline-by-stage', 'half', 'bar', { x: 6, y: 17, w: 6, h: 14 }),
      // Row 4 — my open deals (sortable table; staleness via days-since-update column).
      tile('my-open-deals', 'full', 'table', { x: 0, y: 31, w: 12, h: 18 }),
    ],
  },
  {
    id: 'sales-overview',
    name: 'Sales Overview',
    description: 'Pipeline health, segments and top deals at a glance.',
    icon: 'handshake',
    tiles: [
      tile('pipeline-by-stage', 'half'),
      tile('deals-created-by-rep', 'half'),
      tile('top-by-volume', 'full'),
    ],
  },
]

export const getDashboard = (id: string): Dashboard | undefined =>
  DASHBOARDS.find((d) => d.id === id)
